import { cn, NativeSelect } from "@nexxt/ui";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, Layers } from "lucide-react";
import { m } from "../../../paraglide/messages.js";
import { kitQueries } from "../brand-kit/kit.queries";
import { useBrandLens } from "./use-brand-lens";

// The chip of the lens in the frame (ADR-306), always in view: the logo of the brand's kit and its name, over a native
// select, which the keyboard, a screen reader and a phone's picker all know. "All brands", "My brands", the brands of
// the user's crews, then every brand.

export function BrandLensSelect({ className }: { className?: string }) {
  const lens = useBrandLens();
  const kits = useQuery(kitQueries.list());
  const logo =
    lens.lens.kind === "brand" ? kits.data?.items.find((kit) => kit.brandOrgId === lens.value)?.logoUrl : undefined;
  const mine = lens.brands.filter((b) => b.mine);
  const known = lens.lens.kind !== "brand" || lens.brands.some((b) => b.id === lens.value);
  return (
    <span
      className={cn(
        "relative flex h-(--control-h) max-w-40 min-w-0 shrink items-center gap-1.5 rounded-full border border-input bg-background px-2.5 text-sm has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring",
        lens.lens.kind !== "all" && "border-primary/60 bg-accent",
        className,
      )}
    >
      {logo ? (
        <img src={logo} alt="" className="size-5 shrink-0 rounded-sm bg-muted object-contain" />
      ) : (
        <Layers aria-hidden className="size-4 shrink-0 text-muted-foreground" />
      )}
      <span aria-hidden className="min-w-0 truncate">
        {lens.label}
      </span>
      <ChevronDown aria-hidden className="size-4 shrink-0 text-muted-foreground" />
      <NativeSelect
        aria-label={m.lens_label()}
        value={lens.value}
        onChange={(e) => lens.choose(e.target.value as typeof lens.value)}
        className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
      >
        <option value="all">{m.lens_all()}</option>
        <option value="mine">{m.lens_mine()}</option>
        {!known && <option value={lens.value}>{lens.label}</option>}
        {mine.length > 0 && (
          <optgroup label={m.lens_group_mine()}>
            {mine.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </optgroup>
        )}
        <optgroup label={m.lens_group_all()}>
          {lens.brands.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
        </optgroup>
      </NativeSelect>
    </span>
  );
}
